import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { useLang } from "@/landing/LanguageContext";
import { images } from "@/landing/data";
import { Reveal, Overline } from "@/landing/ui";

export const MethodSection = () => {
  const { t } = useLang();
  const m = t.method;
  return (
    <section id="metodo" className="scroll-mt-20 bg-cream py-24 text-carbon lg:py-32" data-testid="method-section">
      <div className="mx-auto max-w-6xl px-6 lg:px-10">
        <Reveal className="mb-14 max-w-3xl">
          <Overline>{m.overline}</Overline>
          <h2 className="mt-5 font-serif text-3xl font-light leading-tight md:text-4xl lg:text-5xl">
            {m.title}
          </h2>
          <p className="mt-7 font-sans text-base font-light leading-relaxed text-earth lg:text-lg">
            {m.intro}
          </p>
        </Reveal>

        <div className="border-t border-earth/20">
          {m.pillars.map((p, i) => (
            <Reveal key={p.n} delay={(i % 2) * 0.06}>
              <div className="grid grid-cols-1 gap-6 border-b border-earth/20 py-10 lg:grid-cols-12 lg:gap-12 lg:py-14">
                <div className="lg:col-span-4">
                  <span className="font-serif text-5xl font-light text-earth/30">{p.n}</span>
                  <h3 className="mt-4 font-serif text-2xl font-light text-carbon lg:text-3xl">
                    {p.title}
                  </h3>
                </div>
                <div className="space-y-4 lg:col-span-8">
                  {p.desc.map((d, j) => (
                    <p key={j} className="font-sans text-base font-light leading-relaxed text-earth lg:text-lg">
                      {d}
                    </p>
                  ))}
                  {p.items && (
                    <ul className="mt-5 space-y-3">
                      {p.items.map((it, k) => (
                        <li key={k} className="flex items-start gap-3">
                          <Check size={18} className="mt-1 shrink-0 text-earth" />
                          <span className="font-sans text-base font-light leading-relaxed text-carbon lg:text-lg">
                            {it}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <p className="mx-auto mt-14 max-w-3xl text-center font-serif text-2xl font-light italic leading-snug text-carbon lg:text-3xl">
            {m.closing}
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export const LearnSection = () => {
  const { t } = useLang();
  const l = t.learn;
  return (
    <section id="aprender" className="scroll-mt-20 bg-beige py-24 text-carbon lg:py-32" data-testid="learn-section">
      <div className="mx-auto max-w-6xl px-6 lg:px-10">
        <Reveal className="mb-14 max-w-2xl">
          <Overline>{l.overline}</Overline>
          <h2 className="mt-5 font-serif text-3xl font-light leading-tight md:text-4xl lg:text-6xl">
            {l.title}
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2">
          {l.items.map((item, i) => (
            <Reveal key={i} delay={(i % 2) * 0.05}>
              <div className="flex items-center gap-5 border-b border-earth/20 py-6">
                <span className="font-serif text-base text-earth/50">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <Check size={18} className="shrink-0 text-earth" />
                <span className="font-sans text-base font-light text-carbon lg:text-lg">{item}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export const ExperienceSection = () => {
  const { t } = useLang();
  const e = t.experience;
  return (
    <section className="bg-earth py-24 text-cream lg:py-32" data-testid="experience-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal className="mb-14 max-w-3xl">
          <Overline dark>{e.overline}</Overline>
          {e.title && (
            <h2 className="mt-5 font-serif text-3xl font-light leading-tight text-balance md:text-4xl lg:text-5xl">
              {e.title}
            </h2>
          )}
        </Reveal>

        <div className="mb-14 grid grid-cols-1 gap-6 md:grid-cols-2">
          {e.formats.map((f, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="h-full border border-cream/15 p-10 transition-colors duration-300 hover:border-sand/50">
                <span className="font-sans text-xs tracking-[0.25em] uppercase text-sand">
                  {f.tag}
                </span>
                <h3 className="mt-4 font-serif text-3xl font-light text-cream lg:text-4xl">
                  {f.label}
                </h3>
                <p className="mt-4 font-sans text-base font-light text-cream/65">{f.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="flex gap-4 overflow-x-auto pb-4 hide-scrollbar">
            {images.reels.map((src, i) => (
              <motion.div
                key={i}
                whileHover={{ y: -8 }}
                transition={{ duration: 0.4 }}
                className="relative aspect-[9/16] w-44 shrink-0 overflow-hidden sm:w-56"
              >
                <img src={src} alt="" className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-earth/90 via-transparent to-transparent" />
              </motion.div>
            ))}
          </div>
          <p className="mt-6 font-sans text-xs tracking-[0.15em] uppercase text-cream/50">{e.note}</p>
        </Reveal>
      </div>
    </section>
  );
};
